import React, { useState, useEffect } from 'react';
import { FiHome, FiClock, FiUser, FiSun, FiTool, FiMail, FiUsers, FiFileText, FiShield } from 'react-icons/fi';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Language_i18 from '../../js/useNationalLanguage.jsx';
import { normalize_latest_version } from '../../js/latest_version.js';
import { Cookies } from '../../shared/cookies.js';
import { useSelector, useDispatch } from 'react-redux';
import { update_open_button_support } from '../../order/orders/actions.js';

const iconBySource = {
    '💡': FiSun, '🛠': FiTool, '✉': FiMail, '🤝': FiUsers,
    '📜': FiFileText, '🛡': FiShield, '🕘': FiClock, '🕒': FiClock,
    '⏰': FiClock, '👩': FiUser, '👤': FiUser, '🙎‍♀️': FiUser,
};

const iconByLabel = {
    Diary: FiClock, Profile: FiUser, 'Nhật ký': FiClock, 'Hồ sơ': FiUser,
};

function MenuList({ items, switch_router, national_market, isCollapsed }) {
    const { i18n, lang } = Language_i18();
    const [openKeys, setOpenKeys] = useState(new Set());
    const [selectedKey, setSelectedKey] = useState(null);
    const national_market_check = Cookies.get('national_market');
    const latest_version_raw = Cookies.get('latest_version') || '';
    const latest_version_normalized = normalize_latest_version(latest_version_raw);
    const latest_version_text = latest_version_normalized === '' ? '' : String(latest_version_normalized);
    const latest_version_number = Number(latest_version_normalized);
    const has_latest_version = latest_version_raw !== '';
    const open_button_support = useSelector(state => state.open_button_support);
    const dispatch = useDispatch();


    const toggle = key => {
        setOpenKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };
    const matchesLatestVersion = (item) => {
        if (!Array.isArray(item?.latest_version)) return true;
        if (!has_latest_version) return false;
        return item.latest_version.some((version) => {
            const normalizedVersion = normalize_latest_version(version);
            if (String(normalizedVersion) === latest_version_text) return true;
            if (Number.isNaN(latest_version_number)) return false;
            return Number(normalizedVersion) === latest_version_number;
        });
    };

    const renderItems = (list, parentKey = '') => {
        const visibleItems = list.filter(matchesLatestVersion);
        return visibleItems.map((item, idx) => {
            const key = parentKey === '' ? `${idx}` : `${parentKey}-${idx}`;
            const childItems = Array.isArray(item.data) ? item.data.filter(matchesLatestVersion) : [];
            const hasChildren = childItems.length > 0;
            const isOpen = openKeys.has(key);
            const isSelected = selectedKey === key;
            const isNextRoute =
                typeof item.url_redirect === 'string' &&
                item.url_redirect.includes('/next/');
            const isSupportRoute =
                item.url_redirect === "/shop/support" ||
                item.url_redirect === "/next/support";
            const canToggleSupport =
                item.url_redirect === "/shop/support" ||
                (item.url_redirect === "/next/support" && (Cookies.get('apikey') || '').length > 2);
            const isForceAnchorRoute =
                typeof item.url_redirect === 'string' &&
                (item.url_redirect === '/ai/utility/home_ai' ||
                    item.url_redirect === '/shop/category/tips_vip');
            const isAnchorRoute = isNextRoute || isForceAnchorRoute;
            const isReactLinkRoute =
                typeof item.url_redirect === 'string' &&
                item.url_redirect.startsWith('/reactapp/') &&
                item.url_mode !== 'redirect';

            const menuButtonClassName =
                `w-full flex items-center justify-between py-1.5 px-3 text-slate-900 visited:text-slate-900 no-underline ` +
                // pink bg if open or selected, gray otherwise
                `${(isOpen || isSelected) ? 'bg-pink-100' : 'bg-gray-100'} bg-opacity-50 hover:bg-opacity-75 ` +
                // pink left border whenever open or selected
                `${(isOpen || isSelected) ? 'border-l-2 border-pink-400 ' : ''}` +
                `${isCollapsed ? '!justify-center px-2 ' : ''}` +
                // font tweaks when selected
                `${isSelected ? 'font-[600]' : ''}`;

            const labelForIcon = typeof item.label === 'object'
                ? (item.label[i18n.language] || item.label.vi)
                : item.label;
            const sourceKey = typeof item.icon_src === 'string'
                ? item.icon_src.replace(/\uFE0F/g, '')
                : '';
            const MappedIcon = iconBySource[sourceKey] || iconByLabel[labelForIcon];

            const menuContent = (
                <div className={`${isCollapsed ? 'w-auto justify-center' : 'w-full justify-between'} flex items-center`}>
                    <div className={`${isCollapsed ? 'w-7 justify-center' : ''} flex min-w-0 items-center`}>
                    {item.iconType === "SlHome" && <FiHome className={`${isCollapsed ? 'h-7 w-7' : 'h-5 w-5'} ${isCollapsed ? 'mr-0' : 'mr-2'}`} />}
                    {item.iconType === "FaClock" && <FiClock className={`${isCollapsed ? 'h-7 w-7' : 'h-5 w-5'} ${isCollapsed ? 'mr-0' : 'mr-2'}`} />}
                    {item.icon_src && (
                        item.icon_src.includes("/")
                            ? <img src={item.icon_src} alt="" className={`${isCollapsed ? 'h-7 w-7' : 'h-5 w-5'} ${isCollapsed ? 'mr-0' : 'mr-2'}`} />
                            : MappedIcon
                                ? <MappedIcon className={`${isCollapsed ? 'h-7 w-7' : 'h-5 w-5'} ${isCollapsed ? 'mr-0' : 'mr-2'}`} />
                                : <span className={`${isCollapsed ? 'h-7 w-7 text-[22px]' : 'h-5 w-5 text-[22px]'} ${isCollapsed ? 'mr-0' : 'mr-2'} flex items-center justify-center leading-none`}>{item.icon_src}</span>
                    )}
                    <div
                        className={
                            `${isCollapsed ? 'hidden ' : ''}flex-1 whitespace-normal break-words text-left ` +
                            (isSelected ? 'font-[600]' : '')
                        }
                        style={{ fontSize: '15px' }}
                    >
                        {typeof item.label === 'object'
                            ? (item.label[i18n.language] || item.label.vi)
                            : item.label}
                        {
                            !open_button_support && isSupportRoute && canToggleSupport && (
                                <span className="ml-1 inline-block bg-purple-400 text-white text-sm font-bold px-1 py-0 rounded-full scale-90">
                                    ⏶
                                </span>

                            )
                        }
                        {
                            open_button_support && isSupportRoute && canToggleSupport && (
                                <span className="ml-1 inline-block bg-red-400 text-white text-sm font-bold px-1 py-0 rounded-full scale-90">
                                    ⏷
                                </span>
                            )
                        }
                    </div>
                    </div>
                    {hasChildren && !isCollapsed && (
                        isOpen
                            ? <FiChevronUp className="ml-2 h-4 w-4 shrink-0" />
                            : <FiChevronDown className="ml-2 h-4 w-4 shrink-0" />
                    )}
                </div>
            );

            return (
                <div key={key} className="w-full border-t border-gray-300 last:border-b" style={{ width: '100%' }}>
                    {hasChildren ? (
                        <button
                            className={menuButtonClassName}
                            onClick={() => {
                                setSelectedKey(key);
                                toggle(key);
                            }}
                        >
                            {menuContent}
                        </button>
                    ) : isReactLinkRoute ? (
                        <Link
                            to={item.url_redirect}
                            className={menuButtonClassName}
                            onClick={(event) => {
                                setSelectedKey(key);
                                if (item.url_redirect === '/reactapp/' && !national_market_check) {
                                    event.preventDefault();
                                    window.location.href = '/';
                                }
                            }}
                        >
                            {menuContent}
                        </Link>
                    ) : isAnchorRoute ? (
                        <a
                            href={item.url_redirect}
                            className={menuButtonClassName}
                            onClick={(e) => {
                                setSelectedKey(key);
                                if (item.url_redirect === '/next/support' && (Cookies.get('apikey') || '').length > 2) {
                                    e.preventDefault();
                                    dispatch(update_open_button_support(!open_button_support));
                                } else if (isForceAnchorRoute && item.url_redirect) {
                                    e.preventDefault();
                                    switch_router(item.url_redirect);
                                }
                            }}
                        >
                            {menuContent}
                        </a>
                    ) : (
                        <button
                            className={menuButtonClassName}
                            onClick={() => {
                                setSelectedKey(key);
                                if (item.url_redirect == '/reactapp/') {
                                    if (!national_market_check) {
                                        window.location.href = '/';
                                    } else {
                                        switch_router(item.url_redirect);
                                    }
                                } else if (item.url_mode === 'redirect') {
                                    window.open(item.url_redirect, '_blank');
                                } else if (item.url_redirect) {
                                    switch_router(item.url_redirect);
                                }
                            }}
                        >
                            {menuContent}
                        </button>
                    )}

                    {hasChildren && isOpen && !isCollapsed && (
                        <div className="pl-2">
                            {renderItems(childItems, key)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return <div className="flex flex-col">{renderItems(items)}</div>;
}

export default MenuList;
